import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  FormHelperText,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import RpVirtualList from "@/components/virtual/RpVirtualList";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";
import { flattenGroupSections } from "../utils/locationManagementGroupTypes";

const GROUP_OPTION_ROW_HEIGHT = 40;
const GROUP_OPTION_LIST_MAX_HEIGHT = 240;

const uniqueValues = (values = []) => [...new Set(values.map(String))];

const normalizeGroupOption = (group, sectionType = "Groups") => ({
  ...group,
  value: String(group.value),
  label: group.label,
  type: group.type || sectionType,
});

const buildSections = (groupSections = [], groupOptions = [], lockedSet) => {
  const sectionMap = new Map();
  const seenGroupIds = new Set();

  const ensureSection = (type) => {
    const sectionType = type || "Groups";
    if (!sectionMap.has(sectionType)) {
      sectionMap.set(sectionType, { type: sectionType, groups: [] });
    }
    return sectionMap.get(sectionType);
  };

  groupSections.forEach((section) => {
    (section.groups || []).forEach((group) => {
      if (!group?.value || !group?.label) return;

      const option = normalizeGroupOption(group, section.type);
      if (seenGroupIds.has(option.value)) return;

      ensureSection(section.type).groups.push({
        ...option,
        disabled: group.disabled || lockedSet.has(option.value),
      });
      seenGroupIds.add(option.value);
    });
  });

  groupOptions.forEach((option) => {
    if (!option?.value || !option?.label) return;

    const groupValue = String(option.value);
    if (seenGroupIds.has(groupValue)) return;

    const sectionType = option.type || option.groupTypeName || "Groups";
    ensureSection(sectionType).groups.push({
      ...option,
      value: groupValue,
      type: sectionType,
      disabled: option.disabled || lockedSet.has(groupValue),
    });
    seenGroupIds.add(groupValue);
  });

  return [...sectionMap.values()].filter((section) => section.groups.length);
};

const LocationFormGroupFilter = ({
  groupSections = [],
  groupOptions = [],
  value = [],
  onChange,
  onBlur,
  placeholder = "Groups",
  noOptionsText = "No groups found.",
  lockedGroupIds = [],
  error = false,
  helperText = "",
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const selectedValues = useMemo(() => uniqueValues(value), [value]);
  const [pendingValues, setPendingValues] = useState(selectedValues);
  const pendingSet = useMemo(() => new Set(pendingValues), [pendingValues]);
  const lockedSet = useMemo(
    () => new Set((lockedGroupIds || []).map(String)),
    [lockedGroupIds]
  );

  useEffect(() => {
    if (!open) {
      setPendingValues(selectedValues);
    }
  }, [open, selectedValues]);

  const sections = useMemo(
    () => buildSections(groupSections, groupOptions, lockedSet),
    [groupOptions, groupSections, lockedSet]
  );
  const allGroups = useMemo(() => flattenGroupSections(sections), [sections]);

  const selectedGroups = useMemo(() => {
    const optionByValue = new Map(
      allGroups.map((group) => [String(group.value), group])
    );

    return selectedValues.map((groupId) => {
      const option = optionByValue.get(groupId);
      return {
        value: groupId,
        label: option?.label || groupId,
      };
    });
  }, [allGroups, selectedValues]);

  const visibleSections = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return sections
      .filter(
        (section) =>
          !selectedTypes.length || selectedTypes.includes(section.type)
      )
      .map((section) => ({
        ...section,
        groups: (section.groups || []).filter((group) =>
          normalizedSearch
            ? group.label.toLowerCase().includes(normalizedSearch) ||
              section.type.toLowerCase().includes(normalizedSearch)
            : true
        ),
      }))
      .filter((section) => section.groups.length);
  }, [searchValue, sections, selectedTypes]);

  const visibleGroups = useMemo(
    () => flattenGroupSections(visibleSections),
    [visibleSections]
  );
  const clearableSelectedValues = useMemo(
    () => selectedValues.filter((groupId) => !lockedSet.has(groupId)),
    [lockedSet, selectedValues]
  );
  const clearablePendingValues = useMemo(
    () => pendingValues.filter((groupId) => !lockedSet.has(groupId)),
    [lockedSet, pendingValues]
  );
  const hasClearableSelection = clearableSelectedValues.length > 0;
  const hasClearablePendingSelection = clearablePendingValues.length > 0;
  const hasPendingChanges = useMemo(() => {
    if (pendingValues.length !== selectedValues.length) return true;

    return pendingValues.some((groupId) => !selectedValues.includes(groupId));
  }, [pendingValues, selectedValues]);
  const groupListHeight = Math.min(
    visibleGroups.length * GROUP_OPTION_ROW_HEIGHT,
    GROUP_OPTION_LIST_MAX_HEIGHT
  );

  const handleClose = () => {
    setPendingValues(selectedValues);
    setOpen(false);
    setSearchValue("");
    onBlur?.();
  };

  const handleSearchChange = (event) => {
    const nextValue = event.target.value;
    if (shouldBlockUnsafeSearchInput(nextValue)) return;
    setSearchValue(nextValue);
  };

  const handleChange = (nextValue) => {
    setPendingValues(uniqueValues(nextValue));
  };

  const handleToggleGroup = (group) => {
    const groupValue = String(group.value);
    if (group.disabled || lockedSet.has(groupValue)) return;

    handleChange(
      pendingSet.has(groupValue)
        ? pendingValues.filter((valueItem) => valueItem !== groupValue)
        : [...pendingValues, groupValue]
    );
  };

  const handleSelectAllVisible = () => {
    const visibleSelectableIds = visibleGroups
      .filter((group) => !group.disabled && !lockedSet.has(String(group.value)))
      .map((group) => String(group.value));

    handleChange([...pendingValues, ...visibleSelectableIds]);
  };

  const handleClearSelection = () => {
    handleChange(pendingValues.filter((groupId) => lockedSet.has(groupId)));
  };

  const handleTriggerClear = (event) => {
    event.stopPropagation();
    const nextValues = selectedValues.filter((groupId) =>
      lockedSet.has(groupId)
    );

    setPendingValues(nextValues);
    setSelectedTypes([]);
    onChange?.(nextValues);
    onBlur?.();
  };

  const handleToggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type)
        ? prev.filter((selectedType) => selectedType !== type)
        : [...prev, type]
    );
  };

  const handleApply = () => {
    if (!hasPendingChanges) return;

    onChange?.(pendingValues);
    setOpen(false);
    setSearchValue("");
    onBlur?.();
  };

  const renderGroupOption = (group) => {
    const isSelected = pendingSet.has(String(group.value));

    return (
      <MenuItem
        key={group.value}
        onClick={() => handleToggleGroup(group)}
        disabled={group.disabled}
        sx={{
          bgcolor: isSelected ? "var(--primary-tint)" : "transparent",
          minHeight: 40,
          px: 1.75,
          py: 0.75,
          "&:hover": {
            bgcolor: isSelected
              ? "var(--primary-tint-hover)"
              : "var(--neutral-200)",
          },
          "&.Mui-disabled": {
            opacity: 0.55,
          },
        }}
      >
        <Checkbox
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          checked={isSelected}
          disabled={group.disabled}
          size="small"
          sx={{
            color: "var(--neutral-500)",
            pl: 0,
            pr: 1.25,
            py: 0,
            "&.Mui-checked": { color: "var(--primary-base)" },
          }}
        />
        <ListItemText
          primary={group.label}
          slotProps={{
            primary: {
              color: "var(--neutral-700)",
              fontSize: 13,
              fontWeight: 400,
              lineHeight: "19.5px",
            },
          }}
        />
      </MenuItem>
    );
  };

  return (
    <Box>
      <Box
        component="button"
        type="button"
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          alignItems: "center",
          bgcolor: "var(--neutral-white)",
          border: "1px solid",
          borderColor: error
            ? "var(--status-error)"
            : "color-mix(in srgb, var(--palette-common-black) 23%, transparent)",
          borderRadius: "var(--radius-control)",
          color: "var(--neutral-700)",
          cursor: "pointer",
          display: "flex",
          font: "inherit",
          gap: 0.75,
          mt: "8px",
          minHeight: 40,
          px: 1.25,
          py: 0.5,
          textAlign: "left",
          width: "100%",
        }}
      >
        <Box
          component="span"
          sx={{
            alignItems: "center",
            display: "flex",
            flex: 1,
            gap: 0.5,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {selectedGroups.length ? (
            <>
              {selectedGroups.slice(0, 2).map((group) => (
                <Box
                  key={group.value}
                  component="span"
                  sx={{
                    alignItems: "center",
                    bgcolor:
                      "color-mix(in srgb, var(--palette-common-black) 8%, transparent)",
                    borderRadius: "var(--radius-control)",
                    color: "var(--neutral-700)",
                    display: "inline-block",
                    fontSize: 13,
                    fontWeight: 400,
                    height: 24,
                    justifyContent: "flex-start",
                    lineHeight: "23.5px",
                    maxWidth: 120,
                    minWidth: 0,
                    overflow: "hidden",
                    px: 1,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {group.label}
                </Box>
              ))}
              {selectedGroups.length > 2 ? (
                <Box
                  component="span"
                  sx={{
                    alignItems: "center",
                    bgcolor:
                      "color-mix(in srgb, var(--palette-common-black) 8%, transparent)",
                    borderRadius: "var(--radius-control)",
                    color: "var(--neutral-700)",
                    display: "inline-block",
                    fontSize: 13,
                    fontWeight: 400,
                    height: 24,
                    lineHeight: "23.5px",
                    px: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  +{selectedGroups.length - 2}
                </Box>
              ) : null}
            </>
          ) : (
            <Typography
              component="span"
              sx={{
                color: "var(--neutral-600)",
                fontSize: 14,
                lineHeight: "20px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {placeholder}
            </Typography>
          )}
        </Box>
        {hasClearableSelection ? (
          <Box
            component="span"
            aria-label="Clear selected groups"
            title="Clear selected groups"
            onClick={handleTriggerClear}
            sx={{
              alignItems: "center",
              borderRadius: "var(--radius-control)",
              color: "var(--neutral-700)",
              display: "inline-flex",
              flexShrink: 0,
              height: 20,
              justifyContent: "center",
              width: 20,
              "&:hover": {
                bgcolor:
                  "color-mix(in srgb, var(--palette-common-black) 8%, transparent)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </Box>
        ) : null}
        <KeyboardArrowDownIcon
          fontSize="small"
          sx={{
            color: "var(--neutral-500)",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Box>
      {helperText ? (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      ) : null}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1500, width: { xs: "calc(100vw - 32px)", sm: 498 } }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              border: "1px solid var(--neutral-200)",
              borderRadius: "var(--radius-container)",
              boxShadow: "var(--shadow-floating)",
              mt: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "space-between",
                px: 1.75,
                py: 1,
              }}
            >
              <Button
                variant="text"
                size="small"
                onClick={handleSelectAllVisible}
                disabled={!visibleGroups.length}
                sx={{
                  fontSize: 11.2,
                  fontWeight: 500,
                  lineHeight: "19.6px",
                  minWidth: 64,
                  textTransform: "none",
                }}
              >
                Select All
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleClearSelection}
                disabled={!hasClearablePendingSelection}
                sx={{
                  borderRadius: "var(--radius-control)",
                  fontSize: 11.2,
                  fontWeight: 500,
                  lineHeight: "19.6px",
                  minWidth: 64,
                  py: 0.25,
                  textTransform: "none",
                }}
              >
                Clear Selection
              </Button>
            </Box>
            <Divider />

            <Box sx={{ maxHeight: 402, overflowY: "auto", px: 2, py: 1.75 }}>
              <Typography
                sx={{
                  color: "var(--neutral-700)",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: "19.92px",
                  mb: 1,
                }}
              >
                Filter by Group
              </Typography>
              <TextField
                value={searchValue}
                onChange={handleSearchChange}
                fullWidth
                size="small"
                placeholder="Search.."
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--radius-control)",
                    minHeight: 45,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "var(--neutral-600)",
                    opacity: 1,
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Typography
                sx={{
                  color: "var(--neutral-700)",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: "19.92px",
                  mb: 1.75,
                }}
              >
                Sort Groups By Type
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {sections.map((section) => {
                  const isSelected = selectedTypes.includes(section.type);

                  return (
                    <Button
                      key={section.type}
                      variant="outlined"
                      size="small"
                      onClick={() => handleToggleType(section.type)}
                      sx={{
                        bgcolor: isSelected
                          ? "var(--surface-dark)"
                          : "var(--neutral-white)",
                        borderColor: isSelected
                          ? "var(--surface-dark)"
                          : "var(--neutral-200)",
                        borderRadius: "var(--radius-control)",
                        color: isSelected
                          ? "var(--neutral-white)"
                          : "var(--neutral-700)",
                        fontSize: 13,
                        fontWeight: 400,
                        height: 24,
                        lineHeight: "19.5px",
                        minWidth: "auto",
                        px: 1,
                        py: 0,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: isSelected
                            ? "var(--surface-dark)"
                            : "var(--neutral-200)",
                          borderColor: isSelected
                            ? "var(--surface-dark)"
                            : "var(--neutral-200)",
                        },
                      }}
                    >
                      {section.type}
                    </Button>
                  );
                })}
              </Box>

              {visibleGroups.length ? (
                <Box sx={{ height: groupListHeight, minHeight: 40 }}>
                  <RpVirtualList
                    items={visibleGroups}
                    useWindowScroll={false}
                    style={{ height: "100%", overflowX: "hidden" }}
                    renderItem={renderGroupOption}
                  />
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: "var(--neutral-600)",
                    fontSize: 13,
                    py: 2,
                  }}
                >
                  {noOptionsText}
                </Typography>
              )}
            </Box>

            <Divider sx={{ borderColor: "var(--neutral-200)" }} />
            <Box sx={{ pb: 1.75, pt: 2, px: 1.75 }}>
              <Button
                variant="contained"
                fullWidth
                disabled={!hasPendingChanges}
                onClick={handleApply}
                sx={{
                  borderRadius: "var(--radius-control)",
                  fontSize: 12,
                  fontWeight: 500,
                  height: 46,
                  lineHeight: "21px",
                  minHeight: 46,
                  textTransform: "none",
                  "&.Mui-disabled": {
                    bgcolor:
                      "color-mix(in srgb, var(--palette-common-black) 12%, transparent)",
                    color:
                      "color-mix(in srgb, var(--palette-common-black) 26%, transparent)",
                  },
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default LocationFormGroupFilter;
